import {
  AvailableMonth,
  getAvailableAttendanceMonths,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { attendanceToTempAttendance } from "../attendanceUtils";

export function useAttendanceMobile() {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    attendanceMonth,
    selectedMonth,
    selectedYear,
    setAttendanceMonth,
    setTempAttendanceMonth,
    setSelectedMonth,
    setSelectedYear,
    isSubmitted,
    submittedAt,
  } = useContext(AttendancesContext);
  const navigate = useNavigate();

  const [versionRestoreBottomSheetOpen, setVersionRestoreBottomSheetOpen] =
    useState(false);
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>();

  useEffect(() => {
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    getAvailableAttendanceMonths({
      query: { mandate_id: selectedMandateId },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAvailableMonths(data);
    });
  }, [lang, selectedMandateId]);

  function onSaveDraft() {
    if (selectedMandateId === null) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (attendanceMonth === undefined) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "Tried to save attendance draft, but attendanceMonth was undefined",
      );
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    saveAttendance({
      query: { draft: false },
      body: {
        data: attendanceMonth.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
      toast(translate("changesSavedSuccessfully", lang));
    });
  }

  function onSelectMonth(selectedOption: string) {
    // the month and year are stored in a string in the format "<month> <year>"
    const [monthString, yearString] = selectedOption.split(" ");
    const month = Number.parseInt(monthString);
    const year = Number.parseInt(yearString);

    if (Number.isNaN(month) || Number.isNaN(year)) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "The selected month or year or both are not valid integers.",
      );
    }

    setSelectedMonth(month);
    setSelectedYear(year);
  }

  return {
    lang,
    versionRestoreBottomSheetOpen,
    setVersionRestoreBottomSheetOpen,
    navigate,
    attendanceMonth,
    onSaveDraft,
    onSelectMonth,
    availableMonths,
    selectedMonth,
    selectedYear,
    isSubmitted,
    submittedAt,
  };
}
